/*
 * CONFIRMATION SCREEN — Shown after successful submission
 * Design: Quiet Reverence — warm, dignified, reassuring
 */

import { CheckCircle, Heart } from "lucide-react";

interface Props {
  projectId: string;
  email: string;
  name: string;
}

export default function StepConfirmation({ projectId, email, name }: Props) {
  return (
    <div className="step-enter flex flex-col items-center text-center space-y-8 py-6">
      {/* Icon */}
      <div className="relative">
        <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
          <CheckCircle className="w-12 h-12 text-primary" />
        </div>
        <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-card border-2 border-background flex items-center justify-center">
          <Heart className="w-4 h-4 text-primary fill-primary" />
        </div>
      </div>

      {/* Headline */}
      <div className="space-y-3">
        <h2 className="font-['Playfair_Display'] font-700 text-3xl text-foreground leading-tight">
          Their Story Is<br />In Good Hands
        </h2>
        <p className="text-muted-foreground text-base max-w-sm">
          We have received everything and our team is beginning production on {name ? `${name}'s` : 'your'} tribute.
        </p>
      </div>

      {/* Ornamental divider */}
      <div className="ornament-divider w-full max-w-xs">
        <span className="text-xs font-['Playfair_Display'] italic text-muted-foreground">What happens next</span>
      </div>

      {/* Timeline */}
      <div className="w-full max-w-sm space-y-4 text-left">
        <TimelineItem
          step="1"
          title="Our Team Gets to Work"
          description="Our production team carefully reviews your media and begins crafting the tribute."
        />
        <TimelineItem
          step="2"
          title="Director Review"
          description="Our director personally reviews and approves the final cut before it leaves our studio."
        />
        <TimelineItem
          step="3"
          title="Your Personal Link"
          description="You will receive a secure, personal link to your finished tribute when it is ready."
        />
      </div>

      {/* Project ID */}
      <div className="w-full max-w-sm p-4 rounded-xl bg-muted/50 border border-border space-y-1">
        <p className="text-xs text-muted-foreground">Your Project Reference</p>
        <p className="font-mono text-sm font-medium text-foreground tracking-wide">{projectId}</p>
        <p className="text-xs text-muted-foreground">Confirmation sent to {email}</p>
      </div>

      <p className="text-xs text-muted-foreground max-w-xs">
        Questions? Contact us at{' '}
        <a href="mailto:support@goldenchronicle.org" className="text-primary underline underline-offset-2">
          support@goldenchronicle.org
        </a>
      </p>
    </div>
  );
}

function TimelineItem({ step, title, description }: { step: string; title: string; description: string }) {
  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center">
        <div className="w-7 h-7 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
          <span className="text-xs font-semibold text-primary">{step}</span>
        </div>
        {step !== '3' && <div className="w-px flex-1 bg-border mt-1" />}
      </div>
      <div className="pb-4">
        <p className="font-medium text-foreground text-sm">{title}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
    </div>
  );
}
